import { v4 as uuidv4 } from "uuid";
import { NormalizeParkRedemption } from "@helpers/albumContractAdapter";

/**
 * Returns the unique visual-group key for a room reservation.
 *
 * API collections may be separated by commercial service while still belonging
 * to the same reservation. The visual group intentionally excludes ServiceId.
 *
 * @param {Object} photoDetails Parent photo details.
 * @param {Object} experience Album experience.
 * @returns {string} Reservation group key.
 */
const BuildRoomReservationKey = (
    photoDetails = {},
    experience = {}
) => {
    const reservation = experience?.Reservation || {};
    const bookId = reservation.BookId ?? reservation.BookID ?? "";
    const room = reservation.Room ?? photoDetails.Room ?? "";

    return [
        String(bookId),
        String(room),
        String(photoDetails.ArrivalDate || ""),
        String(photoDetails.DepartureDate || ""),
    ].join("|");
};

/**
 * Returns a stable identifier for one album experience.
 *
 * @param {Object} experience Album experience.
 * @returns {string} Experience identifier.
 */
const BuildRoomExperienceKey = (experience = {}) => {
    return String(
        experience?.AlbumKey?.Id ||
        experience?.Id ||
        [
            experience?.Park?.Id || "",
            experience?.ContentType ?? experience?.AlbumType ?? "",
            experience?.VisitDate || "",
            experience?.ServiceId || "",
        ].join("|")
    );
};

export default {
    methods: {
        /**
         * Gets the albums associated with a room reservation.
         *
         * @param {string|number} room Room number.
         * @param {string} arrivalDate Guest arrival date.
         * @param {string} departureDate Guest departure date.
         * @param {string} guestName Guest name.
         * @returns {Promise<void>} Resolves when the albums are loaded.
         */
        async GetAlbumByRoom(room, arrivalDate, departureDate, guestName = "") {
            const parameters = {
                type: 2,
                params: {
                    Room: room,
                    ArrivalDate: arrivalDate,
                    DepartureDate: departureDate,
                    GuestName: guestName || "",
                },
            };

            const response = await this.$store.dispatch("getAlbums", parameters);
            const hasAlbumExperiences = (
                Array.isArray(response) ? response : []
            ).some(
                (collection) =>
                    Array.isArray(collection?.PhotoExperiences) &&
                    collection.PhotoExperiences.length > 0
            );

            if (!hasAlbumExperiences) {
                this.$store.dispatch("setLoading", false);
                Vue.$toast.open({
                    message: this.$t(
                        "Validations.reservation_dont_have_multimedios"
                    ),
                    type: "error",
                });
                return;
            }

            this.FormatAlbumsByRoom(response);
            this.$store.dispatch("setReservationsTab", true);
            this.$store.dispatch("setTypeCodeTab", 2);

            Vue.$toast.open({
                message: this.$t("messages.album_add_successfully"),
                type: "success",
            });

            if (response.length && this.$route.name !== "albumes") {
                this.$router.push({ name: "albumes" });
            }
        },

        /**
         * Groups room collections by reservation without losing service context.
         *
         * @param {Array<Object>} data API room collections.
         * @returns {void}
         */
        FormatAlbumsByRoom(data) {
            const reservationGroups = new Map();

            for (const collection of Array.isArray(data) ? data : []) {
                const photoDetails = collection?.PhotoDetails || {};
                const experiences = Array.isArray(
                    collection?.PhotoExperiences
                )
                    ? collection.PhotoExperiences
                    : [];

                for (const experience of experiences) {
                    const reservation = experience?.Reservation || {};
                    const reservationKey = BuildRoomReservationKey(
                        photoDetails,
                        experience
                    );

                    if (!reservationGroups.has(reservationKey)) {
                        reservationGroups.set(reservationKey, {
                            uuid: uuidv4(),
                            code:
                                reservation.Room ??
                                photoDetails.Room ??
                                "",
                            BookId:
                                reservation.BookId ??
                                reservation.BookID ??
                                null,
                            ArrivalDate: photoDetails.ArrivalDate,
                            DepartureDate: photoDetails.DepartureDate,
                            ExpiresDate: experience.ExpiresDate || null,
                            GuestName: photoDetails.GuestName || "",
                            getCode: [],
                            park: [],
                            Type: photoDetails.Type,
                            parkRedemption: NormalizeParkRedemption(
                                photoDetails.ParkRedemption
                            ),
                            _experienceKeys: new Set(),
                        });
                    }

                    const album = reservationGroups.get(reservationKey);
                    const experienceKey = BuildRoomExperienceKey(experience);

                    if (album._experienceKeys.has(experienceKey)) {
                        continue;
                    }

                    album._experienceKeys.add(experienceKey);
                    album.park.push(experience);

                    if (!album.ExpiresDate && experience.ExpiresDate) {
                        album.ExpiresDate = experience.ExpiresDate;
                    }
                }
            }

            const albums = Array.from(reservationGroups.values()).map(
                (album) => {
                    const { _experienceKeys, ...publicAlbum } = album;
                    return publicAlbum;
                }
            );

            if (albums.length) {
                this.$store.dispatch("setAlbum", albums);
            }
        },

    },
};
